import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap"
import DashboardCard from "../../DashboardComponents/DashboardCard"
import ListItem from "../../DashboardComponents/ListItem"
import ProgressBar from "../../DashboardComponents/ProgressBar"
import CustomButton from "../../OnboardingComponents/CustomButton"
import { ICONS } from "../../../constants/icons"
import { getUserAvatarUrl } from "../../../helpers/userAvatar"
import {
  formatDisplayName,
  getUserFullName,
  nameWithYou,
} from "../../../helpers/userDisplay"
import { formatDurationFromHours } from "../../../helpers/formatDuration"

const CADENCE_OPTIONS = [
  { key: "week", label: "Per Week" },
  { key: "day", label: "Per Day" },
]

const DEFAULT_DAYS_PER_WEEK = 7
const SCROLL_LOAD_THRESHOLD_PX = 48

const normalizeCadence = value => {
  const raw = String(value || "")
    .toLowerCase()
    .trim()
  if (raw === "per_day" || raw === "day") return "day"
  return "week"
}

const resolveDaysPerWeek = daysPerWeek => {
  const value = Number(daysPerWeek)
  if (!Number.isFinite(value) || value <= 0) return DEFAULT_DAYS_PER_WEEK
  return Math.min(DEFAULT_DAYS_PER_WEEK, value)
}

const toDisplayHours = (hours, sourceCadence, targetCadence, daysPerWeek) => {
  const amount = Number(hours) || 0
  const source = normalizeCadence(sourceCadence)
  const weekDays = resolveDaysPerWeek(daysPerWeek)
  if (source === targetCadence) return amount
  if (source === "week" && targetCadence === "day") {
    return amount / weekDays
  }
  if (source === "day" && targetCadence === "week") {
    return amount * weekDays
  }
  return amount
}

const formatRateHours = hours => {
  const value = Number(hours) || 0
  if (!Number.isFinite(value)) return "0"
  const rounded = Math.round(value * 100) / 100
  if (Number.isInteger(rounded)) return String(rounded)
  return rounded.toFixed(2).replace(/\.?0+$/, "")
}

const getItemDisplayName = item => {
  const base =
    getUserFullName(item?.user) || formatDisplayName(item?.name) || ""
  if (!base) return item?.user?.email || "Unknown"
  if (item?.name && /\(You\)/i.test(String(item.name))) {
    return nameWithYou(base, true)
  }
  return base
}

/**
 * Estimated hours list with infinite scroll inside a fixed-height panel.
 */
const EstimatedHoursCard = ({
  list = [],
  total = 0,
  fullHeight = false,
  showProgress = false,
  tone = "neutral",
  hasMore = false,
  isLoadingMore = false,
  onLoadMore,
  /** Actual project days in a "week" window (capped at 7). Short projects use real span. */
  daysPerWeek = DEFAULT_DAYS_PER_WEEK,
}) => {
  const [cadence, setCadence] = useState("week")
  const listRef = useRef(null)
  const loadingLockRef = useRef(false)
  const weekDays = resolveDaysPerWeek(daysPerWeek)
  const activeLabel =
    CADENCE_OPTIONS.find(option => option.key === cadence)?.label || "Per Week"

  const rows = useMemo(
    () =>
      (list || []).map(item => {
        const displayHours = toDisplayHours(
          item?.estimated_hours,
          item?.hours_cadence,
          cadence,
          weekDays,
        )
        return {
          ...item,
          displayName: getItemDisplayName(item),
          displayHours,
        }
      }),
    [list, cadence, weekDays],
  )

  // Prefer project-level total so partial infinite pages don't under-count.
  const displayTotal = useMemo(() => {
    const fromProp = Number(total)
    if (Number.isFinite(fromProp) && fromProp > 0) return fromProp
    return rows.reduce((sum, row) => sum + (Number(row.displayHours) || 0), 0)
  }, [rows, total])

  const tryLoadMore = useCallback(() => {
    if (!hasMore || isLoadingMore || loadingLockRef.current) return
    if (typeof onLoadMore !== "function") return
    loadingLockRef.current = true
    onLoadMore()
  }, [hasMore, isLoadingMore, onLoadMore])

  useEffect(() => {
    if (!isLoadingMore) loadingLockRef.current = false
  }, [isLoadingMore])

  // If the list doesn't overflow, scroll never fires — keep loading until it does or ends.
  useEffect(() => {
    const el = listRef.current
    if (!el || !hasMore || isLoadingMore) return
    if (el.scrollHeight <= el.clientHeight + SCROLL_LOAD_THRESHOLD_PX) {
      tryLoadMore()
    }
  }, [rows.length, hasMore, isLoadingMore, tryLoadMore])

  const handleScroll = event => {
    const el = event.currentTarget
    if (!el) return
    const remaining = el.scrollHeight - el.scrollTop - el.clientHeight
    if (remaining <= SCROLL_LOAD_THRESHOLD_PX) {
      tryLoadMore()
    }
  }

  return (
    <DashboardCard
      title="Estimated Hours"
      tone={tone}
      fullHeight={fullHeight}
      className="project-overview__estimated-card"
      topRight={
        <UncontrolledDropdown className="project-overview__estimated-cadence">
          <DropdownToggle
            tag={CustomButton}
            variant="secondary"
            size="sm"
            type="button"
            trailingIconSrc={ICONS.chevronDown}
            trailingIconAlt=""
            style={{ backgroundColor: "#E1F2E8" }}
          >
            {activeLabel}
          </DropdownToggle>
          <DropdownMenu end>
            {CADENCE_OPTIONS.map(option => (
              <DropdownItem
                key={option.key}
                active={option.key === cadence}
                onClick={() => setCadence(option.key)}
              >
                {option.label}
              </DropdownItem>
            ))}
          </DropdownMenu>
        </UncontrolledDropdown>
      }
    >
      <div
        ref={listRef}
        className="project-overview__estimated-list"
        role="list"
        aria-busy={isLoadingMore}
        onScroll={handleScroll}
      >
        {rows.length === 0 ? (
          <p className="project-overview__empty">No estimates yet.</p>
        ) : (
          rows.map(item => (
            <ListItem
              key={item.id || `${item.displayName}-${item.estimated_hours}`}
              avatarName={item.displayName}
              avatarSrc={getUserAvatarUrl(item?.user)}
              title={item.displayName}
              subtitle={
                showProgress ? (
                  <ProgressBar
                    value={item.progress || 0}
                    max={100}
                    tone="green"
                    className="project-overview__estimated-progress"
                  />
                ) : undefined
              }
              trailing={
                <span className="project-overview__hours-text">
                  {formatRateHours(item.displayHours)}h/
                  {cadence === "day" ? "day" : "week"}
                </span>
              }
              bordered
            />
          ))
        )}
        {isLoadingMore ? (
          <div
            className="project-overview__collaborator-skeleton"
            aria-hidden="true"
          >
            <span className="project-overview__collaborator-skeleton-avatar" />
            <span className="project-overview__collaborator-skeleton-name" />
            <span className="project-overview__collaborator-skeleton-role" />
          </div>
        ) : null}
      </div>

      <div className="project-overview__total">
        <span className="project-overview__total-label">
          Total Estimated Hours
        </span>
        <span className="project-overview__total-value">
          {formatDurationFromHours(displayTotal)}
        </span>
      </div>
    </DashboardCard>
  )
}

export default EstimatedHoursCard
